import type { ReactNode } from 'react';

export type TextAlign = 'left' | 'right' | 'center';

export interface DataTableColumn<T> {
  id: string;
  header: ReactNode;
  accessor: (row: T) => ReactNode;
  width?: number | string;
  align?: TextAlign;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];

  isLoading?: boolean;
  error?: ReactNode;

  page?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;

  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;

  emptyState?: ReactNode;
  className?: string;
}

function getAlignClass(align?: TextAlign): string {
  if (align === 'right') {
    return 'text-right';
  }
  if (align === 'center') {
    return 'text-center';
  }
  return 'text-left';
}

export function DataTable<T>({
  columns,
  data,
  isLoading,
  error,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onRowClick,
  rowClassName,
  emptyState,
  className,
}: DataTableProps<T>): JSX.Element {
  const colSpan = columns.length || 1;
  const tableClasses = ['data-table', className]
    .filter(Boolean)
    .join(' ');

  const showFooter =
    typeof totalCount === 'number' && typeof page === 'number' && typeof pageSize === 'number';
  const startRow = showFooter ? page * pageSize + 1 : 0;
  const endRow = showFooter ? Math.min(totalCount as number, (page + 1) * pageSize) : 0;
  const hasPrevious = showFooter && page > 0;
  const hasNext = showFooter && (page + 1) * pageSize < (totalCount as number);

  return (
    <div className="data-table__wrapper">
      <table className={tableClasses}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                className={`data-table__cell data-table__cell--header ${getAlignClass(column.align)}`}
                style={column.width ? { width: column.width } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={colSpan} className="data-table__cell data-table__cell--message">
                Loading...
              </td>
            </tr>
          ) : error ? (
            <tr>
              <td colSpan={colSpan} className="data-table__cell data-table__cell--message data-table__cell--error">
                {error}
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className="data-table__cell data-table__cell--message">
                {emptyState ?? 'No data'}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => {
              const rowClass = rowClassName?.(row) ?? undefined;
              const rowClasses = [
                'data-table__row',
                rowIndex % 2 === 0 ? 'data-table__row--even' : 'data-table__row--odd',
                onRowClick ? 'data-table__row--clickable' : null,
                rowClass,
              ]
                .filter(Boolean)
                .join(' ');

              const handleClick = onRowClick ? () => onRowClick(row) : undefined;

              return (
                <tr
                  key={rowIndex}
                  className={rowClasses}
                  role={onRowClick ? 'button' : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={handleClick}
                >
                  {columns.map((column) => (
                    <td
                      key={column.id}
                      className={`data-table__cell ${getAlignClass(column.align)}`}
                      style={column.width ? { width: column.width } : undefined}
                    >
                      {column.accessor(row)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
        {showFooter ? (
          <tfoot>
            <tr>
              <td colSpan={colSpan} className="data-table__cell">
                <div className="data-table__footer">
                  <div className="data-table__range">
                    Rows {startRow}–{endRow} of {totalCount}
                  </div>
                  <div className="data-table__pagination">
                    <button
                      type="button"
                      className="button button-secondary"
                      onClick={hasPrevious && onPageChange ? () => onPageChange(page - 1) : undefined}
                      disabled={!hasPrevious || !onPageChange}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="button button-secondary"
                      onClick={hasNext && onPageChange ? () => onPageChange(page + 1) : undefined}
                      disabled={!hasNext || !onPageChange}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}
